import { KeyboardEvent, useState } from 'react';
import { TextInput } from './TextInput';
import { useTranslation } from '../../i18n/i18n';
import { TextArea } from './TextArea';
import Checkbox from './Checkbox/Checkbox';
import { usePrevious } from '../../hooks/usePrevious';
import { ApiTotals } from '../../types';

export interface OrderNotesModel {
	comment: string;
	discountCode: string;
	discountCodeChanged?: boolean;
	orderCommentChanged?: boolean;
}

type OrderNotesInputProps = {
	totals?: ApiTotals;
	onBlur: (model: OrderNotesModel) => void;
	onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
	onChangeComment: (value: string) => void;
	defaultOrderComment?: string;
	defaultDiscountCode?: string;
};

export const OrderNotesInput = ({
	totals,
	onBlur,
	onKeyPress,
	onChangeComment,
	defaultOrderComment,
	defaultDiscountCode,
}: OrderNotesInputProps): JSX.Element => {
	const t = useTranslation().form;
	const [showDiscountCode, setShowDiscountCode] = useState(!!defaultDiscountCode);
	const [showOrderComment, setShowOrderComent] = useState(!!defaultOrderComment);
	const [comment, setComment] = useState(defaultOrderComment || '');
	const [discountCode, setDiscountCode] = useState(defaultDiscountCode || '');
	const previousComment = usePrevious(comment);
	const previousDiscountCode = usePrevious(discountCode);

	function handleOnBlur() {
		const discountCodeChanged = previousDiscountCode !== discountCode;
		const orderCommentChanged = previousComment !== comment;
		onBlur({
			comment,
			discountCode,
			discountCodeChanged,
			orderCommentChanged,
		});
	}

	function handleChangeComment(e: React.ChangeEvent<HTMLTextAreaElement>) {
		setComment(e.target.value);
		onChangeComment(e.target.value);
	}

	return (
		<div className="mt-2">
			<div className="mb-2">
				<Checkbox
					className="mb-2"
					id="order-comment-checkbox"
					label={t.orderNotes.orderComment}
					checked={showOrderComment}
					onChange={(e) => {
						setComment('');
						setShowOrderComent(e.target.checked);
						onBlur({
							comment: '',
							orderCommentChanged: true,
							discountCodeChanged: false,
							discountCode,
						});
					}}
				/>
				{showOrderComment && (
					<TextArea
						placeholder={t.writeMessageHere}
						value={comment}
						onChange={handleChangeComment}
						name="comment"
						onBlur={() => {
							handleOnBlur();
						}}
					/>
				)}
			</div>
			<div>
				<Checkbox
					className="mb-2"
					id="discount-code-checkbox"
					label={t.orderNotes.discountCode}
					checked={showDiscountCode}
					onChange={(e) => {
						setDiscountCode('');
						setShowDiscountCode(e.target.checked);
						onBlur({
							discountCode: '',
							discountCodeChanged: true,
							orderCommentChanged: false,
							comment,
						});
					}}
				/>
				{showDiscountCode && (
					<TextInput
						placeholder={`${t.discountCode} ${totals && totals.discount < 0 ? t.valid : t.invalid}`}
						value={discountCode}
						onChange={(e) => setDiscountCode(e.target.value)}
						isValid={totals && totals.discount < 0}
						autoFocus={true}
						onKeyDown={onKeyPress}
						name="discountCode"
						onBlur={() => {
							handleOnBlur();
						}}
					/>
				)}
			</div>
		</div>
	);
};
